import { sortBy } from 'lodash';
import { DEFAULT_PAGE_SIZE, Dataset } from '@/components/data-table/helpers';
import { JsonObject } from '@/lib/helpers';
import {
  CreatePrivateModelModel,
  PrivateModelModel,
} from '@/lib/models/private-model/private-model.model';
import {
  CreateResponse,
  DeleteResponse,
  ListResponse,
  PatchResponse,
  SAFE_LIMIT,
} from '@/lib/services';
import { create, deleteApi, get, getAllPaged, patch, post } from '@/lib/services/sp-api.service';
import { getPoliciesByModelIdsV1 } from '@/lib/services/policy-v1.service';
import { getTypedPrivateModel } from '../models/private-model';
import { AgentModel } from '../models/agent.model';
import { getAgentsByModelIds } from './agent.service';
import { ProviderModel } from '../models/provider-model.model';
import { PolicyMeta } from '../models/policy-model-v1';

export type ModelUsage = { policies: PolicyMeta[]; agents: AgentModel[]; inUse: boolean };

export type ModelUsageMap = Map<string, ModelUsage>;

export type PrivateModelTestReport = {
  inferenceSuccessful: boolean;
  messages: string[];
};

const PAGESIZE_LIMIT = 250;

const getModel = (data?: JsonObject): PrivateModelModel =>
  getTypedPrivateModel(new PrivateModelModel(data));

export const getAllPrivateModels = async (): Promise<PrivateModelModel[]> => {
  return getAllPaged('/models/private-models', PAGESIZE_LIMIT).then((data) =>
    data.map((row) => getModel(row))
  );
};

export const getPrivateModels = async (
  page = 1,
  pageSize: number = DEFAULT_PAGE_SIZE,
  modelIds?: string[],
  connectorIds?: string[]
): Promise<Dataset<PrivateModelModel>> => {
  const params: Record<string, string | number | string[]> = {
    page,
    pageSize,
  };

  if (modelIds?.length) {
    params.modelIds = modelIds;
  }

  if (connectorIds) {
    params.connectorIds = connectorIds;
  }

  const { data, count } = (await get('/models/private-models', params)) as ListResponse;
  const rows: PrivateModelModel[] = data.map((row) => getModel(row));

  return {
    page,
    pageSize,
    rows,
    total: count,
  };
};

export const getProviderModels = async (
  connectorIds?: string[]
): Promise<Dataset<ProviderModel>> => {
  const dataset: Dataset<ProviderModel> = {
    page: 0,
    pageSize: SAFE_LIMIT,
    rows: [],
    total: 0,
  };

  const { data } = (await get('/models/provider-models', {
    page: 1,
    pageSize: PAGESIZE_LIMIT,
    connectorIds: connectorIds?.length ? connectorIds.join(',') : undefined,
  })) as ListResponse;

  const providerModels = (data || []).map((data) => new ProviderModel(data));

  const filteredModels = providerModels.filter(({ inputModalities, outputModalities }) => {
    const hasTextInput = inputModalities.includes('text');
    const hasTextOutput = outputModalities.includes('text');
    return hasTextInput && hasTextOutput;
  });

  dataset.rows = sortBy(filteredModels, ['name']);
  dataset.total = filteredModels.length;

  return dataset;
};

export const getPrivateModelById = async (modelId: string): Promise<PrivateModelModel | null> => {
  const response = (await get(`/models/private-models/${modelId}`)) as JsonObject;
  return response ? getModel(response) : null;
};

export const getPrivateModelsByIds = async (modelIds: string[]): Promise<PrivateModelModel[]> =>
  (await getPrivateModels(1, PAGESIZE_LIMIT, modelIds)).rows;

export const getPrivateModelsByConnectorId = async (
  connectorId: string
): Promise<PrivateModelModel[]> =>
  (await getPrivateModels(1, PAGESIZE_LIMIT, undefined, [connectorId])).rows;

export const getModelUsage = async (modelIds: string[]): Promise<ModelUsageMap> => {
  const usage: ModelUsageMap = new Map();

  const [allPolicies, allAgents] = await Promise.all([
    getPoliciesByModelIdsV1(modelIds),
    getAgentsByModelIds(modelIds),
  ]);

  modelIds.forEach((modelId) => {
    const policies = allPolicies
      .filter(
        ({ portal }) =>
          portal.defaultModelId === modelId || portal.availablePrivateModelIds.includes(modelId)
      )
      .map(({ meta }) => meta);

    const agents = allAgents.filter(({ privateModelId }) => privateModelId === modelId);

    usage.set(modelId, { policies, agents, inUse: !!policies.length || !!agents.length });
  });

  return usage;
};

export const createPrivateModel = async (model: PrivateModelModel): Promise<CreateResponse> => {
  const createModel = new CreatePrivateModelModel(model);

  const createProps = { ...createModel._props };

  // azure openai models do not support modelId @todo abstract this
  if (createModel.provider === 'azure') {
    delete createProps.modelId;
  }

  const response = await create('/models/private-models', createProps);

  if (!response.created) {
    return response;
  }

  const newModel = getModel(response.data);

  // ensure status and status message are hydrated
  await testPrivateModelInference(newModel.id);

  response.model = newModel;

  return response;
};

export const patchPrivateModel = async (
  id: string,
  data: Partial<PrivateModelModel>
): Promise<PatchResponse> => {
  // azure openai models do not support modelId @todo abstract this
  if (data.provider === 'azure') {
    delete data.modelId;
  }

  const response = await patch(`/models/private-models/${id}`, data);

  if (!response.patched) {
    return response;
  }

  // ensure status and status message are hydrated per the new config
  await testPrivateModelInference(id);

  return response;
};

export const deletePrivateModel = async (modelId: string): Promise<DeleteResponse> =>
  deleteApi(`/models/private-models/${modelId}`);

export const testPrivateModelInference = async (
  modelId: string
): Promise<PrivateModelTestReport | null> => {
  const response = await post('/models/private-models/test-inference', {
    privateModelId: modelId,
  });

  const { report } = (response || {}) as { report: PrivateModelTestReport };

  return report || null;
};
